/**
 * Feature 257 — how the station board arranges itself.
 *
 * The board used to lay its stations out as a CSS grid with `auto-rows-fr`,
 * which makes every row exactly the same height. Emptiness is a property of a
 * CARD, but height was decided per ROW — so a row holding one busy station and
 * three quiet ones was as tall as the busy one needed, and the three quiet ones
 * each kept a full-height cell reading "Nessun ordine". On the 15-inch till the
 * kitchens actually own that left 106 px per station: two dish lines, with the
 * rest of the queue behind a button.
 *
 * The rule now is the one the kitchen itself gave, on 15/09/2026:
 *
 *   - a station appears ONLY when it has something in it;
 *   - the stations that appear are always in the same order;
 *   - a column is as tall as the whole screen, like the by-table view.
 *
 * So the board is a row of full-height columns, one per station that is
 * cooking, in the venue's order, and it scrolls sideways when there are more
 * than the screen carries — the same shape as `TableBoard`, which the kitchen
 * already reads every day.
 *
 * Four designs were tried before this one, and each was rejected by a MEASURED
 * fault, not by taste. They are kept in research R2/R3 rather than dropped:
 *
 *   1. Collapsing empty grid ROWS. A row collapses only when all of it is
 *      empty, so with the busy stations scattered the saving evaporated.
 *   2. Vertical COLUMNS with a thin strip for the empty ones. Deployed: a strip
 *      gives its height back only to its OWN column, so a column with nothing
 *      cooking wasted all of it — two of four columns used 96 px of 679.
 *   3. BANDS in the venue's order. Deployed: two busy stations far apart became
 *      two bands that split the height between them — 276 px each, of which
 *      187 px was furniture, leaving two dish lines.
 *   4. One GATHERED band of the busy stations with strips above and below.
 *      Deployed: the strips took the names with them when there were many of
 *      them, and a lone working station read as a banner across the screen.
 *
 * What all four had in common is that they kept the empty stations on the
 * board. Not drawing them at all is what finally gives the queue its height.
 *
 * Everything here is PURE: no React, no DOM, no clock, no network.
 */

import { type KdsScale } from "./kds-scale";

/** Statuses that mean a station has nothing left to do with an item. */
const DONE_WITH = new Set(["SERVED", "CANCELLED"]);

/** Viewport width thresholds, in pixels, and the columns each one carries. */
const WIDTH_BREAKPOINTS: ReadonlyArray<readonly [number, number]> = [
  [1280, 4], // the Atlantis till at 1366 lands here
  [1024, 3],
  [768, 2],
  [0, 1],
];

/** Side padding of the board, in pixels (the page's `p-4`, both sides). */
const BOARD_PADDING_PX = 32;
/** The gap between two columns, in pixels (`gap-4`). */
const COLUMN_GAP_PX = 16;
/**
 * How narrow and how wide a station column may be, at each type size.
 *
 * Both bounds move with the chosen size, for the reason the table cards already
 * move with it: a dish line that wraps onto two lines costs more height than a
 * wider column costs width. The floor is the width below which a dish name has
 * no room left beside its button; the ceiling is what keeps a lone working
 * station a column instead of a banner across the screen — that version was
 * deployed and rejected on sight.
 */
const COLUMN_BOUNDS: Record<KdsScale, { min: number; max: number }> = {
  piccolo: { min: 220, max: 340 },
  normale: { min: 260, max: 420 },
  grande: { min: 320, max: 520 },
  enorme: { min: 380, max: 640 },
};

export interface StationLoad {
  station: any;
  orders: any[];
  busy: boolean;
}

/**
 * Does this station still have something to cook?
 *
 * The board's existing rule, kept here so that the decision to DRAW a station
 * and the decision to fill it cannot disagree — a disagreement would show as an
 * empty column, which is the whole thing this feature removes.
 */
export function stationHasWork(
  orders: readonly any[],
  stationId: string,
): boolean {
  return orders.some((order) =>
    order?.items?.some(
      (item: any) =>
        item?.stationId === stationId && !DONE_WITH.has(item?.status),
    ),
  );
}

/** How many columns this viewport width carries side by side. */
export function maxColumnsForWidth(width: number): number {
  // The last breakpoint starts at 0, so one always matches. `?? 1` is the
  // type-checker's fallback, not a branch a caller can reach.
  return WIDTH_BREAKPOINTS.find(([from]) => width >= from)?.[1] ?? 1;
}

/**
 * One entry per station, in the venue's order, each carrying its own orders.
 *
 * The station object is passed through untouched: its name, its colour and its
 * identity are the venue's, not ours.
 */
export function stationLoads(
  stations: readonly any[],
  orders: readonly any[],
): StationLoad[] {
  return stations.map((station) => {
    const own = orders.filter((order) =>
      order?.items?.some(
        (item: any) =>
          item?.stationId === station?.id && !DONE_WITH.has(item?.status),
      ),
    );
    return { station, orders: own, busy: own.length > 0 };
  });
}

/**
 * The columns the board draws: the stations that have something in them.
 *
 * Order is the venue's, always — a station that is cooking appears in the same
 * place relative to the other stations that are cooking, whatever else is
 * happening on the board. A station with nothing in it is not drawn at all, and
 * that is what gives its height to the ones that are working.
 */
export function boardColumns(
  stations: readonly any[],
  orders: readonly any[],
): StationLoad[] {
  return stationLoads(stations, orders).filter((load) => load.busy);
}

/**
 * How wide one column is, in pixels, given how many are being drawn.
 *
 * The columns share the width between them, within two bounds that both came
 * from the real screen:
 *
 *   - never wider than the ceiling for the chosen type size, so a lone working
 *     station is a column and not a banner;
 *   - never narrower than the floor, below which a dish name has no room left
 *     beside its button. Past that the row scrolls sideways instead, exactly as
 *     the by-table view does.
 */
export function columnWidthPx(
  viewportWidth: number,
  count: number,
  scale: KdsScale,
): number {
  const { min, max } = COLUMN_BOUNDS[scale] ?? COLUMN_BOUNDS.normale;
  const usable = Math.max(0, viewportWidth - BOARD_PADDING_PX);
  const columns = Math.max(1, count);
  const share = (usable - COLUMN_GAP_PX * (columns - 1)) / columns;
  return Math.min(max, Math.max(min, Math.floor(share)));
}
